import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { ansCasesHref } from '@/pages/ans/OverviewPage';
import { fetchAnsCollection } from '@/services/ans';

const QUANTILES = ['p10', 'p25', 'p50', 'p75', 'p90'] as const;

/**
 * ANS fine collection: the GRU state, divida ativa, lateness and recovery.
 *
 * A GRU state is a billing state, never a ruling. The instalment amount ANS
 * publishes is an agreement-level aggregate replicated on every case of the
 * agreement, so recovery here reads lump-sum payments only and the page says
 * so where the figure is.
 */
export function AnsCollectionPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'collection'],
    queryFn: fetchAnsCollection,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:collection.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:collection.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ans:collection.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ans/export/collection" label={t('ans:collection.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={{ ...data.status_mix, value: data.status_mix.value?.statuses ?? null }}
          titleKey="ans:collection.status.title"
          helpKey="ans:collection.status.help"
          labelPrefix="ans:collection.statuses."
          rowHref={(code) => ansCasesHref({ credit_status: code })}
        />

        <section className="rounded-lg border bg-card p-4" data-status-figures>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ans:collection.axes.title')}
          </h2>
          <Answer answer={data.status_mix} help={t('ans:collection.axes.help')}>
            {(mix) => (
              <div className="grid grid-cols-2 gap-3">
                <Figure
                  label={t('ans:collection.axes.dividaAtiva')}
                  value={`${number.format(mix.divida_ativa)} (${percent.format(
                    mix.divida_ativa_share,
                  )})`}
                  href={ansCasesHref({ divida_ativa: true })}
                />
                <Figure
                  label={t('ans:collection.axes.judiciallySuspended')}
                  value={number.format(mix.judicially_suspended)}
                />
                <Figure
                  label={t('ans:collection.axes.adminSuspended')}
                  value={number.format(mix.admin_suspended)}
                />
                <Figure label={t('ans:collection.axes.blank')} value={number.format(mix.blank)} />
              </div>
            )}
          </Answer>
        </section>
      </div>

      <FieldGuide i18nKey="ans:collection.guide" level={2} />

      <section className="rounded-lg border bg-card p-4" data-by-track>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ans:collection.tracks.title')}
        </h2>
        <Answer answer={data.by_track} help={t('ans:collection.tracks.help')}>
          {(rows) => (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ans:collection.tracks.track')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:collection.tracks.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:collection.tracks.paid')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:collection.tracks.pending')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:collection.tracks.dividaAtiva')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:collection.tracks.reviewed')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.track} className="border-t" data-track={row.track}>
                      <td className="px-3 py-2 text-foreground">{row.track}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        <Link
                          to={ansCasesHref({ track: row.track })}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {number.format(row.cases)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.paid)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.paid_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.pending)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.divida_ativa)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.reviewed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Answer>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-lateness>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ans:collection.lateness.title')}
          </h2>
          <Answer answer={data.lateness} help={t('ans:collection.lateness.help')}>
            {(l) => (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <Figure
                    label={t('ans:collection.lateness.paid')}
                    value={number.format(l.paid_cases)}
                  />
                  <Figure
                    label={t('ans:collection.lateness.beforeDue')}
                    value={number.format(l.paid_before_due)}
                  />
                  <Figure
                    label={t('ans:collection.lateness.afterDue')}
                    value={`${number.format(l.paid_after_due)} (${percent.format(l.late_share)})`}
                  />
                </div>
                {l.days ? (
                  <dl className="grid grid-cols-5 gap-2 text-sm" data-lateness-days>
                    {QUANTILES.map((q) => (
                      <div key={q} className="rounded-md border bg-muted/30 p-2">
                        <dt className="text-xs text-muted-foreground">{q}</dt>
                        <dd className="font-semibold tabular-nums text-foreground">
                          {t('ans:collection.lateness.days', {
                            n: number.format(l.days?.quantiles[q] ?? 0),
                          })}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </div>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-recovery>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('ans:collection.recovery.title')}
          </h2>
          <Answer answer={data.recovery} help={t('ans:collection.recovery.help')}>
            {(r) => (
              <div className="grid grid-cols-2 gap-3">
                <Figure
                  label={t('ans:collection.recovery.paidWithLump')}
                  value={number.format(r.paid_with_lump)}
                />
                <Figure
                  label={t('ans:collection.recovery.finalOfPaid')}
                  value={currency.format(r.final_total_of_paid)}
                />
                <Figure
                  label={t('ans:collection.recovery.lumpTotal')}
                  value={`${currency.format(r.lump_total)} (${percent.format(
                    r.lump_share_of_final,
                  )})`}
                />
                <Figure
                  label={t('ans:collection.recovery.discounts')}
                  value={currency.format(r.discounts_total)}
                />
              </div>
            )}
          </Answer>
        </section>
      </div>
    </div>
  );
}

function Figure({ label, value, href }: { label: string; value: string; href?: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">
        {href ? (
          <Link to={href} className="text-primary underline-offset-2 hover:underline">
            {value}
          </Link>
        ) : (
          value
        )}
      </div>
    </div>
  );
}
